/**
 * How a part's staves are numbered once its ossias are counted in.
 *
 * The document keeps an ossia as a voice of the bar flagged {@link Voice.ossia}, drawn
 * above the staff it names. MusicXML has no such thing: what it has is a staff, declared
 * for the whole part, whose `<staff-details>` says what the staff is *for* -- and `ossia`
 * is one of the five things a staff can be for. So an ossia leaves this package as a staff
 * of its own, written **immediately above** the staff it offers an alternative to, which is
 * where it is drawn and how a reader that ignores the word still lays the part out right.
 *
 * That shifts every staff number below it, which is why the mapping is built once per part
 * and consulted everywhere a number is written -- the `<staves>` count, each `<clef>`, each
 * `<staff-details>`, the `<staff>` on a note, and the moved `<staff>` of a cross-staff one.
 * Taking any of them from `index + 1` instead would put the notes on the wrong staff.
 */

import type { Part, PartMeasure, Voice } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { toDivisions } from "./common";
import { staffDetailsNode } from "./export-tab";

/** How large an ossia staff is drawn, as a percentage of a full one. */
const OSSIA_STAFF_SIZE = 60;

/** MusicXML staff numbers for one part, 1-based. */
export interface StaffMap {
  /** What `<staves>` declares: the part's own staves and its ossia staves together. */
  readonly count: number;
  /** The number of each of the part's own staves, by staff index. */
  readonly played: readonly number[];
  /** The number of the ossia staff above a staff index, for the staves that have one. */
  readonly ossia: ReadonlyMap<number, number>;
}

/** Which of a part's staves are offered an ossia anywhere in the piece. */
function stavesWithOssia(part: Part): ReadonlySet<number> {
  const staves = new Set<number>();
  for (const measure of Object.values(part.measures)) {
    for (const voice of measure.voices) {
      if (voice.ossia) staves.add(voice.staff);
    }
  }
  return staves;
}

/**
 * The numbering of one part's staves.
 *
 * A part offered no ossia anywhere numbers its staves one to n, which is what every file
 * written before ossias existed says and what this still writes for all but the few parts
 * that carry one.
 */
export function staffMap(part: Part): StaffMap {
  const offered = stavesWithOssia(part);
  const played: number[] = [];
  const ossia = new Map<number, number>();
  let next = 1;
  for (let index = 0; index < part.staves.length; index += 1) {
    if (offered.has(index)) {
      ossia.set(index, next);
      next += 1;
    }
    played.push(next);
    next += 1;
  }
  return { count: next - 1, played, ossia };
}

/**
 * `<staff-details>` for an ossia staff.
 *
 * `<staff-type>ossia</staff-type>` is what the staff *is*; the size is how it is drawn, and
 * it is written as well because a reader that does not know the type still has to draw a
 * small staff above the music rather than a full-sized one beside it. The lines and any
 * tuning are the shadowed staff's: an ossia is read the way the bar under it is read, on a
 * guitar as much as on a violin.
 */
export function ossiaDetailsNode(part: Part, staff: number, number: number): XmlNode {
  const lines = part.staves[staff]?.lines ?? 5;
  const shadowed = staffDetailsNode(lines, null, part.instrument.tuning);
  return el("staff-details", { number }, [
    leaf("staff-type", "ossia"),
    ...(shadowed?.children ?? [leaf("staff-lines", lines)]),
    leaf("staff-size", OSSIA_STAFF_SIZE),
  ]);
}

/** One line of a bar as the file writes it: what to write, where, and under what number. */
export interface VoiceWrite {
  /** The line to write, or `null` for an ossia staff that offers nothing in this bar. */
  readonly voice: Voice | null;
  /** The staff number it is written on, 1-based. */
  readonly staff: number;
  /** The `<voice>` number, 1-based and stable from bar to bar. */
  readonly number: number;
}

/**
 * The lines of a bar in the order the file states them.
 *
 * An ossia is written **before** the first voice on the staff it stands over, because a
 * reader takes the staves of a bar in the order they arrive: Verovio, handed a bar whose
 * first note is not on its first staff, draws that note on the first staff anyway --
 * verified against 6.3.0, where the whole of a piano part landed on the small staff above
 * it. So an ossia staff, once a part has one, says something in every bar of the part, and
 * where the part offers no alternative it holds a bar's rest that is not printed. That is
 * also the ordinary way a multi-staff part is written: a staff a bar says nothing about is
 * the unusual thing, not a staff that rests.
 *
 * The numbers do not follow that order. An ossia is numbered after the voices that are
 * played, so that the line a player reads as voice one is voice one in every bar, whether
 * or not the bar happens to offer an alternative above it. A bar of a part with no ossia
 * anywhere is written exactly as its voices stand, which is every bar of every file
 * written before ossias existed.
 */
export function voiceOrder(measure: PartMeasure, staves: StaffMap): readonly VoiceWrite[] {
  const played: VoiceWrite[] = [];
  const offered: Voice[] = [];
  for (const voice of measure.voices) {
    if (voice.ossia) offered.push(voice);
    else played.push({ voice, staff: playedStaff(staves, voice.staff), number: played.length + 1 });
  }
  if (staves.ossia.size === 0) return played;

  const above = new Map<number, VoiceWrite[]>();
  let next = played.length;
  staves.ossia.forEach((number, staff) => {
    const lines = offered.filter((voice) => voice.staff === staff);
    const writes = lines.map((voice) => {
      next += 1;
      return { voice, staff: number, number: next };
    });
    // The staff is drawn wherever the part has one, so it is written in every bar; a bar
    // that offers nothing above it holds a rest there that is not printed.
    above.set(
      staff,
      writes.length > 0 ? writes : [{ voice: null, staff: number, number: (next += 1) }],
    );
  });

  const order: VoiceWrite[] = [];
  const waiting = new Set(above.keys());
  for (const entry of played) {
    const staff = entry.voice?.staff ?? -1;
    if (waiting.has(staff)) {
      order.push(...(above.get(staff) ?? []));
      waiting.delete(staff);
    }
    order.push(entry);
  }
  // An ossia offered above a staff that is silent in this bar has nothing to stand before.
  for (const staff of waiting) order.push(...(above.get(staff) ?? []));
  return order;
}

/** The number one of the part's own staves is written as. */
function playedStaff(staves: StaffMap, staff: number): number {
  return staves.played[staff] ?? staff + 1;
}

/**
 * A bar's rest on an ossia staff that offers nothing here, written but not printed.
 *
 * It is what keeps the staff present in every bar without drawing an empty small staff
 * through the piece, and it is read back as what it is: a bar's rest alone on an ossia
 * staff says there is no alternative here, so the import drops it again.
 */
export function silentOssiaNode(
  write: VoiceWrite,
  divisions: number,
  measureTicks: number,
  numbered: boolean,
): XmlNode {
  return el("note", { "print-object": "no" }, [
    el("rest", { measure: "yes" }),
    leaf("duration", toDivisions(measureTicks, divisions)),
    leaf("voice", write.number),
    numbered ? leaf("staff", write.staff) : null,
  ]);
}
